import { Applicant, EmploymentStatus, FullApplication, Income } from "models/FullApplication"

/**
 * Hands a small, whitelisted slice of someone's application answers to the split fact find.
 *
 * This is a copy of `apps/discovery/src/lib/journeyHandoff.ts` in the monorepo, which is where it
 * is read (the split fact find reads it on mount, then clears it). The two have to agree on the
 * storage key, the shape, and the values each field may hold, or the split fact find throws the
 * whole handoff away without saying so (see `parseFields`). Change them together.
 *
 * Linking to the split fact find is a full page load, which throws away this app's state. So the
 * page linking out writes the whitelisted fields just before navigating (`writeHandoff`), and the
 * flow it lands on reads them. Entries are scoped to one application (`friendlyId`) and expire
 * quickly.
 *
 * Only add fields that the split fact find actually uses. Anything readable by scripts on this
 * origin can read this key, so keep it to the minimum: applicant names are personal data, so don't
 * add contact details, dates of birth, addresses or financial amounts. The one address here is the
 * current home of someone selling it, which the Selling section shows back to them, and the one
 * amount is the deposit, which the deposit page shows and validates its breakdown against.
 *
 * `HandoffFields` is their whole contract, but this app can't fill all of it. What it sends:
 *
 * - `applicants` and `numberOfDependents` from the application.
 * - `deposit` from `borrowingDetails`. Remortgages don't have one, so it is usually left out.
 * - `depositSources` never: the funnel sends them to Salesforce and keeps nothing on the
 *   application, so there is nothing here to read.
 * - `sellingCurrentHome` never, and so `currentAddress` never either. The nearest thing the
 *   application has is `currentPropertyStatus`, which says whether their property is listed, not
 *   whether they are keeping it. Sending it wrongly as `true` would show someone a Selling section
 *   that doesn't apply to them, so it is left out and the split fact find skips that section (see
 *   `SplitFactFindContext`). `currentAddress` is only read inside that section, so sending it
 *   would put an address in storage that nothing uses.
 */
export const JOURNEY_HANDOFF_KEY = "mojo-journey-handoff"

// The handoff only has to survive one page load, so anything older than this is stale.
const MAX_AGE_MS = 10 * 60 * 1000

const MAX_APPLICANTS = 2

const MAX_DEPENDENTS = 20

const MAX_ADDRESS_LENGTH = 300

// Well above any realistic deposit. Only here to reject nonsense coming back out of storage.
const MAX_DEPOSIT = 100_000_000

/** The monorepo's `EmploymentStatus` (packages/types/src/funnel/enums.ts), which the reader checks. */
export const HANDOFF_EMPLOYMENT_STATUSES = [
  "permanent-employment",
  "temporary-employment",
  "agency",
  "sole-trader",
  "director-of-a-ltd-company",
  "partner-in-an-llp",
  "contractor",
  "retired",
  "not-working",
] as const
export type HandoffEmploymentStatus = (typeof HANDOFF_EMPLOYMENT_STATUSES)[number]

/** The monorepo's `DepositSource` (packages/types/src/funnel/enums.ts), which the reader checks. */
export const HANDOFF_DEPOSIT_SOURCES = [
  "savings",
  "property-sale",
  "family-gift",
  "inheritance",
  "other",
] as const
export type HandoffDepositSource = (typeof HANDOFF_DEPOSIT_SOURCES)[number]

const MAX_DEPOSIT_SOURCES = HANDOFF_DEPOSIT_SOURCES.length

/** One applicant's answers. At most two are asked about. */
export type HandoffApplicant = {
  firstName: string
  lastName: string
  /** Left out when the applicant hasn't given an employment status. */
  employmentStatus?: HandoffEmploymentStatus
}

/** The answers the split fact find may read. This list is the whole contract. */
export type HandoffFields = {
  applicants?: HandoffApplicant[]
  /** Left out when there are no dependents. */
  numberOfDependents?: number
  /** Where the deposit is coming from. Left out when none were recorded. */
  depositSources?: HandoffDepositSource[]
  /** True when they're selling their current home as well as buying. Left out otherwise. */
  sellingCurrentHome?: boolean
  /**
   * Their current address as display text, only handed over when they're selling it, as it's the
   * home the Selling section asks about. It's personal data, so it's never stored beyond this.
   */
  currentAddress?: string
  /**
   * The total deposit, which the deposit page shows and validates the breakdown against. Left out
   * when the application doesn't have one, as a remortgage won't.
   */
  deposit?: number
}

type Stored = {
  friendlyId: string
  savedAt: number
  fields: HandoffFields
}

// The application keeps employment as its own enum, and the main fact find's values (which is what
// the split fact find reads) as kebab-case. Anything not listed, such as `Unknown`, is left out.
const EMPLOYMENT_STATUS: Partial<Record<EmploymentStatus, HandoffEmploymentStatus>> = {
  [EmploymentStatus.PermanentEmployment]: "permanent-employment",
  [EmploymentStatus.TemporaryEmployment]: "temporary-employment",
  [EmploymentStatus.Agency]: "agency",
  [EmploymentStatus.SoleTrader]: "sole-trader",
  [EmploymentStatus.DirectorLimitedCompany]: "director-of-a-ltd-company",
  [EmploymentStatus.PartnerLlp]: "partner-in-an-llp",
  [EmploymentStatus.Contractor]: "contractor",
  [EmploymentStatus.Retired]: "retired",
  [EmploymentStatus.NotWorking]: "not-working",
}

/**
 * How the applicant is employed, in the vocabulary the split fact find reads.
 *
 * Income is matched to the applicant by `firstApplicant`, which the application marks on both.
 * That mark is optional on an income row, though, and a strict match would then hand over nothing
 * and have the split fact find ask again. So when nothing matches and there is only one applicant,
 * the first row is used: there is nobody else it could belong to. With more than one applicant an
 * unmarked row is left alone instead, as giving someone the other applicant's employment would be
 * worse than asking.
 */
const employmentStatusOf = (
  applicant: Applicant,
  incomes: Income[],
  soleApplicant: boolean
): HandoffEmploymentStatus | undefined => {
  const matched = incomes.find((income) => income.firstApplicant === applicant.firstApplicant)
  const status = (matched ?? (soleApplicant ? incomes[0] : undefined))?.employmentStatus

  return status ? EMPLOYMENT_STATUS[status] : undefined
}

/**
 * Picks what the split fact find can use out of the application, which is the lead applicant then
 * the second, and how many dependents there are. Never spread the application in here.
 *
 * Not everything in the contract is on this application: see the note at the top of this file for
 * what is left out and why. The split fact find then asks without those answers.
 */
export const pickHandoffFields = (
  application: FullApplication | null | undefined
): HandoffFields => {
  // The lead applicant is first, as the split fact find reads the first one as the lead
  const applicantsLeadFirst = [...(application?.applicants ?? [])]
    .sort((a, b) => Number(!!b.firstApplicant) - Number(!!a.firstApplicant))
    .slice(0, MAX_APPLICANTS)

  const incomes = application?.income ?? []
  const soleApplicant = (application?.applicants?.length ?? 0) === 1

  const applicants = applicantsLeadFirst.flatMap((applicant): HandoffApplicant[] => {
    const firstName = applicant.forename?.trim()
    if (!firstName) return []

    const employmentStatus = employmentStatusOf(applicant, incomes, soleApplicant)

    return [
      {
        firstName,
        lastName: applicant.surname?.trim() ?? "",
        ...(employmentStatus && { employmentStatus }),
      },
    ]
  })

  const dependents = application?.dependentsNumber
  const deposit = application?.borrowingDetails?.deposit

  return {
    ...(applicants.length > 0 && { applicants }),
    // A number the split fact find would reject is left out, rather than losing everything else
    ...(dependents !== undefined &&
      Number.isInteger(dependents) &&
      dependents > 0 &&
      dependents <= MAX_DEPENDENTS && { numberOfDependents: dependents }),
    // A zero deposit counts as none: the deposit page can't be completed without an amount
    ...(deposit && deposit > 0 && deposit <= MAX_DEPOSIT && { deposit }),
  }
}

const isOneOf = <T extends string>(options: readonly T[], value: unknown): value is T =>
  options.includes(value as T)

// Storage is untrusted on the way back in, so rebuild each applicant from known keys rather than
// passing through whatever was stored. Anything malformed discards the whole handoff.
const parseApplicant = (value: unknown): HandoffApplicant | null => {
  if (typeof value !== "object" || value === null) return null

  const { firstName, lastName, employmentStatus } = value as Record<string, unknown>
  if (typeof firstName !== "string" || typeof lastName !== "string") return null
  if (employmentStatus === undefined) return { firstName, lastName }

  return isOneOf(HANDOFF_EMPLOYMENT_STATUSES, employmentStatus)
    ? { firstName, lastName, employmentStatus }
    : null
}

const parseFields = (value: unknown): HandoffFields | null => {
  if (typeof value !== "object" || value === null) return null

  const {
    applicants,
    numberOfDependents,
    depositSources,
    sellingCurrentHome,
    currentAddress,
    deposit,
  } = value as Record<string, unknown>
  const fields: HandoffFields = {}

  if (applicants !== undefined) {
    if (!Array.isArray(applicants) || applicants.length > MAX_APPLICANTS) return null

    const parsed = applicants.map(parseApplicant)
    if (!parsed.every((a): a is HandoffApplicant => a !== null)) return null
    fields.applicants = parsed
  }

  if (numberOfDependents !== undefined) {
    if (
      typeof numberOfDependents !== "number" ||
      !Number.isInteger(numberOfDependents) ||
      numberOfDependents < 0 ||
      numberOfDependents > MAX_DEPENDENTS
    ) {
      return null
    }
    fields.numberOfDependents = numberOfDependents
  }

  if (depositSources !== undefined) {
    if (!Array.isArray(depositSources) || depositSources.length > MAX_DEPOSIT_SOURCES) return null
    if (!depositSources.every((source) => isOneOf(HANDOFF_DEPOSIT_SOURCES, source))) return null
    fields.depositSources = depositSources as HandoffDepositSource[]
  }

  if (sellingCurrentHome !== undefined) {
    if (typeof sellingCurrentHome !== "boolean") return null
    fields.sellingCurrentHome = sellingCurrentHome
  }

  if (currentAddress !== undefined) {
    if (typeof currentAddress !== "string" || currentAddress.length > MAX_ADDRESS_LENGTH)
      return null
    fields.currentAddress = currentAddress
  }

  if (deposit !== undefined) {
    if (
      typeof deposit !== "number" ||
      !Number.isFinite(deposit) ||
      deposit < 0 ||
      deposit > MAX_DEPOSIT
    ) {
      return null
    }
    fields.deposit = deposit
  }

  return fields
}

export const clearHandoff = () => {
  try {
    sessionStorage.removeItem(JOURNEY_HANDOFF_KEY)
  } catch {
    // Storage can be unavailable (e.g. blocked by the browser), and there's nothing to clear then.
  }
}

export const writeHandoff = (friendlyId: string, fields: HandoffFields) => {
  try {
    // Don't leave an empty entry (or a stale one from an earlier application) lying around.
    if (Object.keys(fields).length === 0) {
      clearHandoff()
      return
    }

    const stored: Stored = { friendlyId, savedAt: Date.now(), fields }
    sessionStorage.setItem(JOURNEY_HANDOFF_KEY, JSON.stringify(stored))
  } catch {
    // Storage can be unavailable or full. The split fact find works without the handoff.
  }
}

/**
 * The split fact find's side of it. Nothing in this app reads the handoff: this is here so that
 * what `writeHandoff` stores can be checked against what the split fact find would accept. Like
 * theirs it doesn't delete, so that a double invocation in development can't lose the entry.
 */
export const readHandoff = (friendlyId: string): HandoffFields | null => {
  try {
    const raw = sessionStorage.getItem(JOURNEY_HANDOFF_KEY)
    if (!raw) return null

    const stored = JSON.parse(raw) as Partial<Stored> | null
    // Belongs to a different application than the one in this url.
    if (stored?.friendlyId !== friendlyId) return null
    if (typeof stored.savedAt !== "number" || Date.now() - stored.savedAt > MAX_AGE_MS) return null

    return parseFields(stored.fields)
  } catch {
    return null
  }
}
